import { useCallback } from 'react';
import { Button, InlineStack, Text, Tooltip } from '@shopify/polaris';
import { useTriggerAudit } from '../../hooks/useStoreAudit';

interface Props {
  remaining?: number;
  disabled?: boolean;
}

export function TriggerAuditButton({ remaining, disabled = false }: Props) {
  const { mutate: triggerAudit, isPending, isError, error } = useTriggerAudit();

  const isUnlimited = remaining === Number.MAX_SAFE_INTEGER || remaining === undefined;
  const isExhausted = remaining !== undefined && !isUnlimited && remaining <= 0;
  const shouldDisable = disabled || isExhausted || isPending;

  const handleClick = useCallback(() => {
    triggerAudit(undefined);
  }, [triggerAudit]);

  return (
    <InlineStack gap="300" blockAlign="center">
      <Tooltip
        content={
          isExhausted
            ? 'Monthly audit limit reached. Upgrade your plan to run more audits.'
            : 'Analyse your store for SEO, trust, and performance issues.'
        }
        active={isExhausted}
      >
        <Button
          variant="primary"
          onClick={handleClick}
          loading={isPending}
          disabled={shouldDisable}
        >
          Run Audit
        </Button>
      </Tooltip>

      {!isUnlimited && remaining !== undefined && (
        <Text as="span" variant="bodySm" tone={isExhausted ? 'critical' : 'subdued'}>
          {isExhausted ? 'No audits remaining this month' : `${remaining} audit${remaining !== 1 ? 's' : ''} remaining`}
        </Text>
      )}

      {isError && (
        <Text as="span" variant="bodySm" tone="critical">
          {(error as Error)?.message ?? 'Failed to trigger audit.'}
        </Text>
      )}
    </InlineStack>
  );
}
